// 自评等级（沿用 v1.0.0 定性评价，含亚级）+ 到新版 L0-L5 的归并映射

/**
 * 自评等级对照表
 * code 为对外使用的规范码（输入框输入 L2a / Lv2-a / l2A 均会归一化到 L2a）
 * label 为表格中展示的历史写法（Lv2-a）
 * mapped 为归并到新版 L0-L5 的结果（决策 S2：亚级归并到主级）
 */
export const SELF_LEVELS = [
  {
    code: 'L0', label: 'Lv0', mapped: 'L0',
    desc: '纸质文件。作业所需数据依托于纸质文件的交换实现。',
    mark: '纸质（2D图形）',
    env: '',
    stage: '非结构化',
    mode: '人想人干人改'
  },
  {
    code: 'L1', label: 'Lv1', mapped: 'L1',
    desc: '扫描纸质文档使其影像化，形成影像化数字文档。作业所需的数据由数字文档的交换实现。',
    mark: '2D图形、3D几何',
    env: '',
    stage: '信息化',
    mode: '人想人干人改'
  },
  {
    code: 'L2a', label: 'Lv2-a', mapped: 'L2',
    desc: '根据数据需求，约定数据标准并据此形成结构化文档，作业可以通过线上的数据交换实现。',
    mark: '分阶段、分专业或部门',
    env: 'CDE环境',
    stage: '结构化',
    mode: '人想人干人改'
  },
  {
    code: 'L2b', label: 'Lv2-b', mapped: 'L2',
    desc: '在 Lv2-a 基础上，数据交换实现跨阶段、跨专业和跨部门结构化。',
    mark: '跨阶段、跨专业和跨部门',
    env: 'CDE环境',
    stage: '结构化',
    mode: '人想人干人改'
  },
  {
    code: 'L3a', label: 'Lv3-a', mapped: 'L3',
    desc: '在 Lv2-b 基础上，点对点的数据交换，允许不同数据格式，实现人决策机器执行人工调整。',
    mark: '无平台支持',
    env: 'CDE环境',
    stage: '半自动化',
    mode: '人想机干人调'
  },
  {
    code: 'L3b', label: 'Lv3-b', mapped: 'L3',
    desc: '在 Lv3-b 基础上，依托数字底座，项目可以依托平台开放的数据交换功能，且使用约定统一的数据格式进行作业。',
    mark: '有平台支持',
    env: 'CDE环境',
    stage: '半自动化',
    mode: '人想机干人调'
  },
  {
    code: 'L4', label: 'Lv4', mapped: 'L4',
    desc: '在 Lv3-b 基础上，项目的数据流转由平台根据规则进行自动化的管理与处理。',
    mark: '治理方法论 · 有平台支持',
    env: 'CDE环境',
    stage: '自动化',
    mode: '人定机干'
  },
  {
    code: 'L5', label: 'Lv5', mapped: 'L5',
    desc: '智能化数据驱动。平台利用企业的数据资产沉淀，通过数字化、信息化、智能化等技术，主动应用数据、智能推送数据给项目作业。',
    mark: '智能化',
    env: '',
    stage: '智能化',
    mode: '机想机干机自迭代'
  }
]

/** 规范码 → 归并后的 L0-L5 */
export const SELF_LEVEL_MAP = Object.fromEntries(
  SELF_LEVELS.map((x) => [x.code, x.mapped])
)

/** 规范码 → 表格/定位图上的展示写法 */
export const SELF_LEVEL_LABEL = Object.fromEntries(
  SELF_LEVELS.map((x) => [x.code, x.label])
)

/**
 * 定位图上各气泡的坐标与配色（与 MaturityChart.vue 的内联 SVG 一一对应）
 * 抽到数据层，便于新增亚级时只改一处
 */
export const SELF_LEVEL_NODES = [
  { code: 'L5', cx: 760, cy: 200, fill: '#dbeafe', stroke: '#3b82f6', text: '#1d4ed8' },
  { code: 'L4', cx: 650, cy: 300, fill: '#ffedd5', stroke: '#f97316', text: '#c2410c' },
  { code: 'L3b', cx: 540, cy: 318, fill: '#f5d0fe', stroke: '#c026d3', text: '#a21caf' },
  { code: 'L3a', cx: 430, cy: 348, fill: '#fce7f3', stroke: '#ec4899', text: '#be185d' },
  { code: 'L2b', cx: 320, cy: 372, fill: '#bbf7d0', stroke: '#16a34a', text: '#15803d' },
  { code: 'L2a', cx: 320, cy: 408, fill: '#dcfce7', stroke: '#22c55e', text: '#166534' },
  { code: 'L1', cx: 320, cy: 480, fill: '#fef9c3', stroke: '#eab308', text: '#a16207' },
  { code: 'L0', cx: 320, cy: 600, fill: '#cffafe', stroke: '#06b6d4', text: '#0e7490' }
]

/** 表格单元格合并矩阵：SPAN_MATRIX[行号][列号] = [rowspan, colspan]，列号 0 级别 / 1 描述 / 2 标志 / 3 环境 / 4 阶段 / 5 模式 */
export const SPAN_MATRIX = {
  0: { 2: [1, 2], 3: [1, 0], 5: [4, 1] },
  1: { 2: [1, 2], 3: [1, 0], 5: [0, 0] },
  2: { 3: [5, 1], 4: [2, 1], 5: [0, 0] },
  3: { 3: [0, 0], 4: [0, 0], 5: [0, 0] },
  4: { 3: [0, 0], 4: [2, 1], 5: [2, 1] },
  5: { 3: [0, 0], 4: [0, 0], 5: [0, 0] },
  6: { 3: [0, 0] },
  7: { 2: [1, 2], 3: [1, 0] }
}
